import {
    styled,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TableSortLabel,
} from '@mui/material';
import { api, formationInitialeEntryType } from '../../../lib/api/api';
import { IconButton } from '../../../components/IconButton';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useAlert } from '../../../lib/alert';
import { Query } from '../../../components/Query';
import { useNavigate } from 'react-router-dom';
import { pathHandler } from '../../../lib/pathHandler';
import { useState } from 'react';
import { convertFrenchDateToISO } from '../../../lib/date';

type orderByColumnType = 'createdAt';
type orderByType = { column: orderByColumnType; direction: 'asc' | 'desc' };

function FormationInitialeEntryTable() {
    const { displayAlert } = useAlert();
    const [orderBy, setOrderBy] = useState<orderByType | undefined>({
        column: 'createdAt',
        direction: 'desc',
    });
    const navigate = useNavigate();
    return (
        <Query queryKey={['formation-initiale-entries']} apiCall={api.getFormationInitialeEntries}>
            {(data) => {
                const sortedFormationInitialeEntries = sortData(data);
                return (
                    <>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>
                                        <TableSortLabel
                                            active={orderBy?.column === 'createdAt'}
                                            direction={orderBy?.direction}
                                            onClick={buildSortHandler('createdAt')}
                                        >
                                            Date réception
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell>Origine</TableCell>
                                    <TableCell>Prénom</TableCell>
                                    <TableCell>Nom</TableCell>
                                    <TableCell>Date de naissance</TableCell>
                                    <TableCell>Nationalité / Lieu de naissance</TableCell>
                                    <TableCell>E-mail</TableCell>
                                    <TableCell>Téléphone</TableCell>
                                    <TableCell>Adresse</TableCell>
                                    <TableCell>Études 2024/2025</TableCell>
                                    <TableCell>Promo souhaitée 2025/2026</TableCell>
                                    <TableCell></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {sortedFormationInitialeEntries.map((formationInitialeEntry) => (
                                    <TableRow>
                                        <TableCell>{formationInitialeEntry.createdAt}</TableCell>
                                        <TableCell>Admin</TableCell>
                                        <TableCell>{formationInitialeEntry.firstName}</TableCell>
                                        <TableCell>{formationInitialeEntry.lastName}</TableCell>
                                        <TableCell>{formationInitialeEntry.birthdate}</TableCell>
                                        <TableCell>{formationInitialeEntry.nationality}</TableCell>
                                        <TableCell>{formationInitialeEntry.email}</TableCell>
                                        <TableCell>{formationInitialeEntry.phoneNumber}</TableCell>
                                        <TableCell>{formationInitialeEntry.address}</TableCell>
                                        <TableCell>{formationInitialeEntry.lastDiploma}</TableCell>
                                        <TableCell>
                                            {formationInitialeEntry.selectedPromo}
                                        </TableCell>
                                        <TableCell>
                                            <CellRow>
                                                <IconButton
                                                    color="primary"
                                                    size="small"
                                                    title="Copier la ligne"
                                                    IconComponent={ContentCopyIcon}
                                                    onClick={() =>
                                                        copyRowToClipboard(formationInitialeEntry)
                                                    }
                                                />
                                                <IconButton
                                                    color="primary"
                                                    size="small"
                                                    title="Aller au formulaire"
                                                    IconComponent={ArrowForwardIcon}
                                                    onClick={() =>
                                                        navigateToEntryForm(
                                                            formationInitialeEntry.id,
                                                        )
                                                    }
                                                />
                                            </CellRow>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </>
                );
            }}
        </Query>
    );

    function sortData(data: formationInitialeEntryType[]) {
        if (orderBy === undefined) {
            return data;
        }
        return data.sort((a, b) => {
            const aValue = a[orderBy.column];
            const bValue = b[orderBy.column];
            if (aValue === bValue) {
                return 0;
            }
            if (orderBy.column === 'createdAt') {
                const aDate = convertFrenchDateToISO(aValue);
                const bDate = convertFrenchDateToISO(bValue);
                const comparison = aDate.localeCompare(bDate);
                return orderBy.direction === 'asc' ? comparison : -comparison;
            }
            const comparison = aValue.localeCompare(bValue);

            return orderBy.direction === 'asc' ? comparison : -comparison;
        });
    }

    function buildSortHandler(orderByColumn: orderByColumnType) {
        return () => {
            if (orderBy === undefined || orderBy.column !== orderByColumn) {
                setOrderBy({ column: orderByColumn, direction: 'asc' });
                return;
            } else {
                switch (orderBy.direction) {
                    case 'asc':
                        setOrderBy({ column: orderByColumn, direction: 'desc' });
                        break;
                    case 'desc':
                        setOrderBy({ column: orderByColumn, direction: 'asc' });
                        break;
                }
            }
        };
    }

    function navigateToEntryForm(id: string) {
        const path = pathHandler.getRoutePath('FORMATION_ENTRY', {
            id,
            kind: 'formation-initiale',
        });
        navigate(path);
    }

    async function copyRowToClipboard(formationInitialeEntry: formationInitialeEntryType) {
        const rowData = [
            formationInitialeEntry.createdAt,
            'Admin',
            formationInitialeEntry.firstName,
            formationInitialeEntry.lastName,
            formationInitialeEntry.birthdate,
            formationInitialeEntry.nationality,
            formationInitialeEntry.email,
            formationInitialeEntry.phoneNumber,
            formationInitialeEntry.address,
            formationInitialeEntry.lastDiploma,
            formationInitialeEntry.selectedPromo,
        ];
        const textToCopy = rowData.join('\t'); // Use tab as a separator for
        try {
            await navigator.clipboard.writeText(textToCopy);
            displayAlert({
                variant: 'success',
                text: 'Ligne copiée dans le presse-papiers.',
            });
        } catch (error) {
            displayAlert({
                variant: 'error',
                text: `Échec de la copie dans le presse-papiers: ${error}`,
            });
        }
    }
}

const CellRow = styled('div')(({ theme }) => ({ display: 'flex' }));

export { FormationInitialeEntryTable };
